import { Redirect, usePathname } from 'expo-router'
import { StyleSheet, View } from 'react-native'
import * as Hooks from '../hooks'
import * as Stores from '../stores'
import { DesignSystem } from '@/src/design-system'
import { useEffect } from 'react'
import { mesaColorNames } from '@/src/common/theme'
import * as Sentry from '@sentry/react-native'

export function PlatformLayout({ children }: { children: React.ReactNode }) {
  const platform = Hooks.useCardApplicationPlatform()
  const setCardApplication = Stores.useCardApplicationFormStore((s) => s.setCardApplication)
  const { activeApplicationId, activeApplicationStatus } = Hooks.useActiveApplication()
  const { createApplicationStatus } = Hooks.useCreateApplicationIfNeeded()
  const currentRoute = usePathname()
  const redirectRoute = Hooks.useEnforcedRoute(currentRoute)

  useEffect(() => {
    if (activeApplicationId && Hooks.isResolvedPlatform(platform)) {
      setCardApplication({ id: activeApplicationId, platform })
    }
  }, [activeApplicationId, platform, setCardApplication])

  const shouldShowLoading = platform === 'LOADING' || activeApplicationStatus === 'NOT_FOUND' // <-- `useCreateApplicationIfNeeded` should handle creating an application, so we'll wait for it to finish

  if (shouldShowLoading) {
    return <DesignSystem.Loading.Screen style={[StyleSheet.absoluteFill, { zIndex: 88 }]} />
  }

  const shouldRedirectOutOfCardApp =
    !Hooks.isResolvedPlatform(platform) ||
    platform === 'HIGHNOTE' ||
    createApplicationStatus.isError ||
    !activeApplicationId // <-- If we don't have an active application after checking for isLoading, there's a problem.

  if (platform === 'HIGHNOTE') {
    Sentry.captureException(
      new Error(
        'App attempted to mount the new Platform card application but the API told us to use Highnote',
      ),
      { extra: { platform, activeApplicationStatus, createApplicationStatus } },
    )
  }

  if (shouldRedirectOutOfCardApp) {
    return <Redirect href="/main/(tabs)/card/cardBenefits" />
  }

  // If there's a redirect required by the logical flow of the application, we need to redirect to the
  // appropriate route.
  if (redirectRoute) {
    return <Redirect href={redirectRoute.route} />
  }

  return (
    <View
      style={{
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
      }}
    >
      {children}
    </View>
  )
}
